import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { OpportunityWeakPointPayload } from '../lib/proposeOpportunityFixes';
import {
  resolveOpportunityWeakPoint,
  resolveOpportunityWeakPointErrorMessage,
} from '../lib/resolveOpportunityWeakPoint';
import { saveBusinessOpportunity } from '../lib/saveBusinessOpportunity';
import type { OpportunityRefinement } from '../types';
import { buildOpportunityIteration } from '../utils/opportunityIterations';
import { newId } from '../utils/id';
import { useAppData } from './AppDataContext';
import { useUser } from './UserContext';

export type HardeningJob = {
  id: string;
  opportunityId: string;
  opportunityName: string;
  weakPointTitle: string;
  status: 'loading' | 'error';
  error: string | null;
};

type OpportunityHardeningContextValue = {
  jobs: HardeningJob[];
  isHardening: (opportunityId: string) => boolean;
  getJobError: (opportunityId: string) => string | null;
  startResolution: (
    opportunityId: string,
    weakPoint: OpportunityWeakPointPayload,
    resolution: string
  ) => Promise<void>;
  dismissError: (jobId: string) => void;
};

const OpportunityHardeningContext = createContext<OpportunityHardeningContextValue | undefined>(
  undefined
);

export function OpportunityHardeningProvider({ children }: { children: ReactNode }) {
  const { user } = useUser();
  const userId = user?.uid ?? '';
  const { businessOpportunities } = useAppData();
  const opportunitiesRef = useRef(businessOpportunities);
  opportunitiesRef.current = businessOpportunities;

  const [jobs, setJobs] = useState<HardeningJob[]>([]);
  const runningIdsRef = useRef(new Set<string>());

  const isHardening = useCallback(
    (opportunityId: string) =>
      jobs.some((job) => job.opportunityId === opportunityId && job.status === 'loading'),
    [jobs]
  );

  const getJobError = useCallback(
    (opportunityId: string) => {
      const job = jobs.find(
        (entry) => entry.opportunityId === opportunityId && entry.status === 'error'
      );
      return job?.error ?? null;
    },
    [jobs]
  );

  const dismissError = useCallback((jobId: string) => {
    setJobs((current) => current.filter((job) => job.id !== jobId));
  }, []);

  const startResolution = useCallback(
    async (
      opportunityId: string,
      weakPoint: OpportunityWeakPointPayload,
      resolution: string
    ) => {
      const trimmedId = opportunityId.trim();
      if (!trimmedId || !userId) return;
      if (runningIdsRef.current.has(trimmedId)) return;

      const opportunity = opportunitiesRef.current?.find((entry) => entry.id === trimmedId);
      const opportunityName = opportunity?.name?.trim() || 'Opportunity';

      runningIdsRef.current.add(trimmedId);
      const jobId = newId();
      setJobs((current) => [
        ...current.filter((job) => !(job.opportunityId === trimmedId && job.status === 'error')),
        {
          id: jobId,
          opportunityId: trimmedId,
          opportunityName,
          weakPointTitle: weakPoint.title,
          status: 'loading',
          error: null,
        },
      ]);

      try {
        const result = await resolveOpportunityWeakPoint(trimmedId, weakPoint, resolution);
        const current = opportunitiesRef.current?.find((entry) => entry.id === trimmedId);
        if (current) {
          const now = new Date().toISOString();
          const userEntry: OpportunityRefinement = {
            id: newId(),
            createdAt: now,
            type: 'user',
            content: `Fix for "${weakPoint.title}": ${resolution.trim()}`,
          };
          const aiEntry: OpportunityRefinement = {
            id: newId(),
            createdAt: now,
            type: 'ai',
            content: result.changeSummary,
          };
          const iteration = buildOpportunityIteration(
            'resolution',
            result.changeSummary,
            result.ideaIntelligence
          );

          await saveBusinessOpportunity(userId, {
            id: current.id,
            name: current.name,
            industry: current.industry,
            ideaSummary: result.ideaSummary,
            status: current.status === 'draft' ? 'refining' : current.status,
            ideaIntelligence: result.ideaIntelligence,
            refinements: [...(current.refinements ?? []), userEntry, aiEntry],
            iterations: [...(current.iterations ?? []), iteration],
            feedback: current.feedback,
            pnl: current.pnl,
            promotedCaseId: current.promotedCaseId,
            existing: current,
          });
        }
        setJobs((currentJobs) => currentJobs.filter((job) => job.id !== jobId));
      } catch (err) {
        const message = resolveOpportunityWeakPointErrorMessage(err);
        setJobs((currentJobs) =>
          currentJobs.map((job) =>
            job.id === jobId ? { ...job, status: 'error', error: message } : job
          )
        );
      } finally {
        runningIdsRef.current.delete(trimmedId);
      }
    },
    [userId]
  );

  const value = useMemo(
    () => ({
      jobs,
      isHardening,
      getJobError,
      startResolution,
      dismissError,
    }),
    [jobs, isHardening, getJobError, startResolution, dismissError]
  );

  return (
    <OpportunityHardeningContext.Provider value={value}>
      {children}
    </OpportunityHardeningContext.Provider>
  );
}

export function useOpportunityHardening(): OpportunityHardeningContextValue {
  const ctx = useContext(OpportunityHardeningContext);
  if (!ctx) {
    throw new Error('useOpportunityHardening must be used within OpportunityHardeningProvider');
  }
  return ctx;
}
